import { z } from 'zod';
import { uuidSchema } from './common.schema';
import { FeedOrderTypeSchema, FeedOrderRouteSchema, FeedOrderStatusSchema, FeedOrderBaseSchema } from './feedOrder.schema';

/**
 * Query for GET /feedPrep/scan
 *
 * `value` — the barcode / MRN / SKU / product name / batch-id to search for.
 * The server auto-detects the domain: it tries exact-match domains first
 * (patient_mrn → receivable → product_sku) and the fuzzy product_name search
 * last, returning the first non-empty result.
 */
export const ScanSearchBySchema = z.enum(['product_name', 'product_sku', 'lot_unit_id', 'receivable', 'batch_bottle_id', 'patient_mrn']);
export type ScanSearchBy = z.infer<typeof ScanSearchBySchema>;

export const FeedPrepScanQuerySchema = z.object({
  value:       z.string().min(1).max(200),
});
export type FeedPrepScanQuery = z.infer<typeof FeedPrepScanQuerySchema>;

/**
 * Single additive (product) attached to an order via feed_order_items.
 */
export const FeedPrepAdditiveSchema = z.object({
  product_id:   z.string(),
  brand_name:   z.string(),
  local_name:   z.string().nullable(),
  form:         z.string().nullable(),
  sku:          z.string().nullable(),
  quantity:     z.string(),
  quantity_unit: z.string(),
});
export type FeedPrepAdditive = z.infer<typeof FeedPrepAdditiveSchema>;

/** Linked mother attached to a patient via mother_infant_links */
export const LinkedMotherSchema = z.object({
  person_id:         z.string(),
  first_name:        z.string(),
  last_name:         z.string(),
  relationship_type: z.string(),
  provides_milk:     z.boolean(),
  available_ml:      z.number(),
});
export type LinkedMother = z.infer<typeof LinkedMotherSchema>;

/** Preferred donor preference for a patient (ordered by priority_order) */
export const PreferredDonorSchema = z.object({
  donor_id:       z.string(),
  first_name:     z.string(),
  last_name:      z.string(),
  priority_order: z.number(),
  available_ml:   z.number(),
});
export type PreferredDonor = z.infer<typeof PreferredDonorSchema>;

/** Consent status for a donor-milk order row in Step 1. */
export const DonorConsentStatusSchema = z.enum(['VALID', 'MISSING', 'NOT_REQUIRED']);
export type DonorConsentStatus = z.infer<typeof DonorConsentStatusSchema>;

/**
 * One row in the scan result — represents a feed order enriched for the prep board.
 */
export const FeedPrepScanResultSchema = z.object({
  order_id:           uuidSchema,
  patient_id:         uuidSchema,
  patient_name:       z.string(),
  mrn:                z.string(),
  base:               FeedOrderBaseSchema.nullable(),
  additives:          z.array(FeedPrepAdditiveSchema),
  volume_per_feed_ml: z.string().nullable(),
  feeds_per_day:      z.number().nullable(),
  total_ml_per_day:   z.string().nullable(),
  calorific_density:  z.string().nullable(),
  order_type:         FeedOrderTypeSchema,
  route:              FeedOrderRouteSchema.nullable(),
  status:             FeedOrderStatusSchema,
  start_date:         z.string(),
  linked_mothers:     z.array(LinkedMotherSchema),
  preferred_donors:   z.array(PreferredDonorSchema),
  donor_consent:      DonorConsentStatusSchema.optional(),
  blocked_reason:     z.string().nullable().optional(),
});
export type FeedPrepScanResult = z.infer<typeof FeedPrepScanResultSchema>;

/** Top-level data envelope for GET /feedPrep/scan. */
export const FeedPrepScanDataSchema = z.object({
  matched_by: ScanSearchBySchema.nullable(),
  /** Set when matched_by is 'receivable': whether the scanned bottle is Mother's or Donor's milk. */
  matched_source: z.enum(['MOTHER', 'DONOR']).nullable().optional(),
  results:    z.array(FeedPrepScanResultSchema),
});
export type FeedPrepScanData = z.infer<typeof FeedPrepScanDataSchema>;

// ── POST /feedPrep/prepare — Start and complete a feed preparation ────────

/** Milk source type when a base involves milk */
export const MilkSourceTypeEnum = z.enum([
  'MOM',             // Mother's own milk
  'LINKED_DONOR',    // Linked donor milk
  'UNLINKED_DONOR',  // Unlinked donor milk
  'FORMULA',         // Formula / nutrition product
  'STERILE_WATER',   // Sterile water — no milk checks
]);
export type MilkSourceType = z.infer<typeof MilkSourceTypeEnum>;

/** Single item (product or milk) used during preparation */
export const FeedPrepItemInputSchema = z.object({
  product_id:         uuidSchema.optional(),        // DynamoDB product ref
  unit_id:            uuidSchema.optional(),        // inventory_units PK (for products)
  milk_inventory_id:  uuidSchema.optional(),        // milk_inventory PK (for milk)
  // milk_batches PK — a MAIN batch picked by batch number. Sent with milk_inventory_id (the bottle resolved at
  // scan, which is deducted) it only marks the item as a main batch; on its own the server picks the fullest bottle.
  milk_batch_id:      uuidSchema.optional(),
  receivable_id:      uuidSchema.optional(),        // fallback when no milk_inventory exists yet (e.g. use_type=FEED)
  // Post-prep remaining; server derives quantity_used = available − remaining_quantity.
  // Intentionally NOT bounded to >= 0 here: a negative value (Used Quantity > Available
  // Quantity) must reach the service layer's checkRemainingQuantityValid(), which has the
  // actual "available" amount and can return a clear, business-friendly validation message.
  // Bounding it here would instead surface a generic/technical "Request validation failed".
  remaining_quantity: z.number(),
  quantity_unit:      z.string().min(1).max(10),    // mL, g, sachet, etc.
  scanned_barcode:    z.string().max(100).optional(),
  // "Restore To" location selected in the UI for this item's remaining (unused) quantity.
  // For product items (unit_id) this is an inventory_locations.location_id; for milk items
  // (milk_inventory_id / receivable_id / milk_batch_id) this is a storage_locations id.
  // Omitted when the row is locked/disposed (nothing left to restore) — the inventory
  // record's existing location is left unchanged in that case.
  restore_location_id: uuidSchema.optional(),
});
export type FeedPrepItemInput = z.infer<typeof FeedPrepItemInputSchema>;

/** Single split container */
export const FeedPrepSplitInputSchema = z.object({
  volume_ml: z.number().positive(),
  expiry_date: z.string().datetime({ offset: true, local: true }).optional(),
  is_restored: z.boolean().optional().default(false),
  restored_from_split_id: uuidSchema.optional(),
  storage_location_id: uuidSchema.optional(),
});

/** Request body for POST /feedPrep/prepare */
export const StartFeedPrepBodySchema = z.object({
  hospital_id:       uuidSchema,
  order_id:          uuidSchema,
  prepared_by:       uuidSchema,
  desired_volume_ml: z.number().positive(),
  route:             z.string().max(50).optional(),

  // Items used in preparation
  items: z.array(FeedPrepItemInputSchema).min(1),

  // Milk source info (required when order base involves milk)
  milk_source_type:  MilkSourceTypeEnum.optional(),
  deviation_reason:  z.string().max(500).optional(),
  donor_id:          uuidSchema.optional(),

  // Splits
  splits: z.array(FeedPrepSplitInputSchema).min(1),

  // Remainder handling (when sum of splits < desired_volume_ml)
  remainder_action:              z.enum(['DISCARD', 'STORE']).optional(),
  remainder_storage_location_id: uuidSchema.optional(),
  remainder_wasted_reason:       z.string().max(500).optional(),
});
export type StartFeedPrepBody = z.infer<typeof StartFeedPrepBodySchema>;

/** Item in the preparation result */
export const FeedPrepItemResultSchema = z.object({
  product_id:       z.string().nullable(),
  brand_name:       z.string(),
  available_volume: z.string(),
  quantity_used:    z.string(),
  quantity_unit:    z.string(),
  caloric_value:    z.string().nullable(),
  restore_to:       z.string(),
});

/** Split in the preparation result */
/**
 * Per-split SEC generation result.
 * Included in the POST /feedPrep/prepare response so the UI can surface
 * SEC failures immediately (manual entry required when status = 'FAILED').
 *
 * - GENERATED: SEC was assembled and written to sec_traceability.
 * - FAILED:    SEC generation encountered an error (DynamoDB down, tenant not
 *              configured, etc.). The split record was still saved. Manual
 *              entry is required.
 * - SKIPPED:   Split is from non-donor (MOM/FORMULA) milk or was DISCARDED;
 *              SEC does not apply.
 */
export const FeedPrepSplitSecSchema = z.object({
  status:         z.enum(['GENERATED', 'FAILED', 'SKIPPED']),
  encoded:        z.string().optional(),        // 40-char SEC string (GENERATED only)
  human_readable: z.string().optional(),        // 'SEC DIS PIS' form  (GENERATED only)
  error_message:  z.string().optional(),        // Short hint for UI  (FAILED only)
});
export type FeedPrepSplitSec = z.infer<typeof FeedPrepSplitSecSchema>;

export const FeedPrepSplitResultSchema = z.object({
  split_sequence: z.number(),
  container_identifier: z.string(),
  volume_ml:      z.string(),
  status:         z.string(),
  label_expiry_at: z.string().nullable(),
  is_expired:     z.boolean(),
  expiry_notes:   z.string().nullable(),
  is_restored:    z.boolean(),
  restored_from_split_id: uuidSchema.nullable(),
  storage_location_id: uuidSchema.nullable(),
  /**
   * SEC generation status for this split.
   * Optional (absent in legacy responses before SEC was implemented).
   */
  sec:            FeedPrepSplitSecSchema.optional(),
});

/** Warning returned alongside a successful preparation */
export const FeedPrepWarningSchema = z.object({
  type:    z.string(),   // e.g. 'MILK_PREFERENCE', 'EXPIRY_NEAR', 'DONOR_CONSENT'
  message: z.string(),
});

/** Full response for POST /feedPrep/prepare */
export const FeedPrepResultSchema = z.object({
  prep_id:            uuidSchema,
  order_id:           uuidSchema,
  container_identifier: z.string().nullable(),
  patient_name:       z.string(),
  mrn:                z.string(),
  status:             z.string(),
  desired_volume_ml:  z.string(),
  items:              z.array(FeedPrepItemResultSchema),
  splits:             z.array(FeedPrepSplitResultSchema),
  remainder_ml:       z.string(),
  remainder_action:   z.string().nullable(),
  remainder_container_identifier: z.string().nullable(),
  warnings:           z.array(FeedPrepWarningSchema),
});
export type FeedPrepResult = z.infer<typeof FeedPrepResultSchema>;

// ── GET /feedPrep/feeds — List feed schedules ─────────────────────────────

export const FeedListQuerySchema = z.object({
  patient_id:  uuidSchema.optional(),
  // Filter: display_status (array); accepts a single value or multiple values.
  // A single query param (?status=DUE) is coerced to an array automatically.
  status:      z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(z.enum(['DUE', 'PENDING', 'COMPLETED', 'CANCELLED', 'SEND_HOME'])).optional(),
  ),
  from_date:   z.string().optional(),   // ISO date e.g. 2026-04-01
  to_date:     z.string().optional(),   // ISO date e.g. 2026-04-30
  page:        z.coerce.number().int().min(1).default(1),
  limit:       z.coerce.number().int().min(1).max(100).default(20),
});
export type FeedListQuery = z.infer<typeof FeedListQuerySchema>;

export const FeedListItemSchema = z.object({
  schedule_id:          uuidSchema,
  order_id:             uuidSchema,
  order_identifier:     z.string().nullable(),
  patient_id:           uuidSchema,
  hospital_id:          uuidSchema,
  prep_id:              z.string().nullable(),
  container_identifier: z.string().nullable(),
  interval_minutes:     z.number(),
  volume_ml_per_feed: z.string(),
  start_time:         z.string(),
  end_time:           z.string().nullable(),
  is_active:          z.boolean(),
  display_status:     z.enum(['DUE', 'PENDING', 'COMPLETED', 'CANCELLED', 'SEND_HOME']),
  patient_name:       z.string(),
  mrn:                z.string(),
  order_base:         z.string().nullable(),
  order_date:         z.string().nullable(),
  route:              z.string().nullable(),
  expiry_at:          z.string().nullable(),
  created_at:         z.string(),
});
export type FeedListItem = z.infer<typeof FeedListItemSchema>;

export const FeedListResponseSchema = z.object({
  items:       z.array(FeedListItemSchema),
  total:       z.number(),
  page:        z.number(),
  limit:       z.number(),
  total_pages: z.number(),
});
export type FeedListResponse = z.infer<typeof FeedListResponseSchema>;

// ── PATCH /feedPrep/splits/:split_id/deactivate ───────────────────────────

export const DeactivateSplitParamsSchema = z.object({
  split_id: uuidSchema,
});
export type DeactivateSplitParams = z.infer<typeof DeactivateSplitParamsSchema>;

export const DeactivateSplitBodySchema = z.object({
  reason: z.string().min(1).max(500),
});
export type DeactivateSplitBody = z.infer<typeof DeactivateSplitBodySchema>;

export const DeactivateSplitResponseSchema = z.object({
  split_id: uuidSchema,
  status:   z.literal('CANCELLED'),
});
export type DeactivateSplitResponse = z.infer<typeof DeactivateSplitResponseSchema>;

// ── PATCH /feedPrep/:prep_id/deactivate ─────────────────────────────────

export const DeactivatePrepParamsSchema = z.object({
  schedule_id: uuidSchema,
});
export type DeactivatePrepParams = z.infer<typeof DeactivatePrepParamsSchema>;

/**
 * `schedule_id` (URL param) is the source of truth. `split_id` below is
 * validated server-side to belong to the same preparation as the schedule —
 * a request with a mismatched split is rejected (400/409) rather than
 * silently cancelling an unrelated preparation's split.
 */
export const DeactivatePrepBodySchema = z.object({
  split_id: uuidSchema,
  reason: z.string().min(1).max(500),
});
export type DeactivatePrepBody = z.infer<typeof DeactivatePrepBodySchema>;

export const DeactivatePrepResponseSchema = z.object({
  prep_id: uuidSchema,
  schedule_id: uuidSchema,
  split_id: uuidSchema,
  status: z.literal('CANCELLED'),
});
export type DeactivatePrepResponse = z.infer<typeof DeactivatePrepResponseSchema>;

// ── GET /feedPrep/stored — List stored (remaining) containers ─────────────

export const StoredSplitsQuerySchema = z.object({
  patient_id:  uuidSchema.optional(),
  order_id:    uuidSchema.optional(),
  page:        z.coerce.number().int().min(1).default(1),
  limit:       z.coerce.number().int().min(1).max(100).default(20),
});
export type StoredSplitsQuery = z.infer<typeof StoredSplitsQuerySchema>;

export const StoredSplitItemSchema = z.object({
  split_id:              uuidSchema,
  prep_id:               uuidSchema,
  split_sequence:        z.number(),
  container_identifier:  z.string().nullable(),
  volume_ml:             z.string(),
  syringe_label_barcode: z.string().nullable(),
  label_expiry_at:       z.string().nullable(),
  storage_location_id:   z.string().nullable(),
  is_expired:            z.boolean(),
  expiry_notes:          z.string().nullable(),
  status:                z.string(),
  patient_name:          z.string(),
  mrn:                   z.string(),
  order_id:              z.string().nullable(),
  order_base:            z.string().nullable(),
  created_at:            z.string(),
});
export type StoredSplitItem = z.infer<typeof StoredSplitItemSchema>;

export const StoredSplitsResponseSchema = z.object({
  items:       z.array(StoredSplitItemSchema),
  total:       z.number(),
  page:        z.number(),
  limit:       z.number(),
  total_pages: z.number(),
  warnings:    z.array(FeedPrepWarningSchema).default([]),
});
export type StoredSplitsResponse = z.infer<typeof StoredSplitsResponseSchema>;

// ── GET /feedPrep/container/:container_identifier — Lookup by container ───

export const ContainerInfoParamsSchema = z.object({
  container_identifier: z.string().min(1).max(20),
});
export type ContainerInfoParams = z.infer<typeof ContainerInfoParamsSchema>;

export const ContainerInfoSplitSchema = z.object({
  split_id:              uuidSchema,
  split_sequence:        z.number(),
  volume_ml:             z.string(),
  status:                z.string(),
  syringe_label_barcode: z.string().nullable(),
  destination_patient_id: uuidSchema.nullable(),
  destination_patient_name: z.string().nullable(),
  destination_mrn:       z.string().nullable(),
  label_expiry_at:       z.string().nullable(),
  is_expired:            z.boolean(),
  expiry_notes:          z.string().nullable(),
  is_restored:           z.boolean(),
  restored_from_split_id: uuidSchema.nullable(),
  delivery_id:           uuidSchema.nullable(),
});

export const ContainerInfoItemSchema = z.object({
  product_id:  uuidSchema.nullable(),
  quantity_used: z.string(),
  quantity_unit: z.string(),
});

export const ContainerInfoResponseSchema = z.object({
  prep_id:              uuidSchema,
  container_identifier: z.string(),
  prep_type:            z.string(),
  status:               z.string(),
  patient_id:           uuidSchema.nullable(),
  patient_name:         z.string().nullable(),
  mrn:                  z.string().nullable(),
  hospital_id:          uuidSchema,
  prepared_by:          uuidSchema,
  actual_volume_ml:     z.string().nullable(),
  route:                z.string().nullable(),
  order_id:             uuidSchema.nullable(),
  order_type:           z.string().nullable(),
  allergy_check_passed: z.boolean().nullable(),
  expiry_check_passed:  z.boolean().nullable(),
  recall_check_passed:  z.boolean().nullable(),
  started_at:           z.string().nullable(),
  completed_at:         z.string().nullable(),
  created_at:           z.string(),
  splits:               z.array(ContainerInfoSplitSchema),
  items:                z.array(ContainerInfoItemSchema),
  mother_id:            uuidSchema.nullable(),
  mother_name:          z.string().nullable(),
  mother_mrn:           z.string().nullable(),
});
export type ContainerInfoResponse = z.infer<typeof ContainerInfoResponseSchema>;

// ── GET /feedPrep/scan-item/:barcode — Universal barcode scan for feed prep ───

export const ScanItemParamsSchema = z.object({
  barcode: z.string().min(1).max(150),
});
export type ScanItemParams = z.infer<typeof ScanItemParamsSchema>;

export const ScanItemQuerySchema = z.object({
  patient_id: uuidSchema.optional(),
});
export type ScanItemQuery = z.infer<typeof ScanItemQuerySchema>;

export const ScanItemResponseSchema = z.object({
  source: z.enum(['MILK_INVENTORY', 'PRODUCT_INVENTORY', 'MILK_BATCH', 'FEED_PREP', 'MILK_RECEIVABLE']),
  // Common identifiers
  identifier: z.string(),
  hospital_id: uuidSchema,
  status: z.string(),
  expiry_at: z.string().nullable(),
  // Milk inventory fields (populated when source = MILK_INVENTORY)
  milk_inventory_id: uuidSchema.nullable(),
  container_barcode: z.string().nullable(),
  container_type: z.string().nullable(),
  initial_volume_ml: z.string().nullable(),
  remaining_volume_ml: z.string().nullable(),
  storage_location_id: uuidSchema.nullable(),
  batch_id: uuidSchema.nullable(),
  batch_number: z.string().nullable(),
  receivable_id: uuidSchema.nullable(),
  receipt_number: z.string().nullable(),
  source_type: z.string().nullable(),
  milk_type: z.string().nullable(),
  provider_id: uuidSchema.nullable(),
  provider_name: z.string().nullable(),
  // Product inventory fields (populated when source = PRODUCT_INVENTORY)
  unit_id: uuidSchema.nullable(),
  product_id: uuidSchema.nullable(),
  sku: z.string().nullable(),
  brand_name: z.string().nullable(),
  local_name: z.string().nullable(),
  form: z.string().nullable(),
  unit_of_measure: z.string().nullable(),
  volume_uom: z.string().nullable(),
  tms_uuid: z.string().nullable(),
  lot_number: z.string().nullable(),
  original_quantity: z.string().nullable(),
  remaining_quantity: z.string().nullable(),
  available_quantity: z.string().nullable(),
  unit: z.string().nullable(),
  unit_status: z.string().nullable(),
  // Milk batch fields (populated when source = MILK_BATCH)
  milk_batch_id: uuidSchema.nullable(),
  volume_ml: z.string().nullable(),
  batch_status: z.string().nullable(),
  // Feed prep fields (populated when source = FEED_PREP)
  prep_id: uuidSchema.nullable(),
  container_identifier: z.string().nullable(),
  prep_type: z.string().nullable(),
  patient_id: uuidSchema.nullable(),
  patient_name: z.string().nullable(),
  prepared_by: z.string().nullable(),
  actual_volume_ml: z.string().nullable(),
  completed_at: z.string().nullable(),
});
export type ScanItemResponse = z.infer<typeof ScanItemResponseSchema>;

// ── GET /feedPrep/:prep_id/splits — All splits for a preparation ──────────────

export const PrepSplitsParamsSchema = z.object({
  prep_id: uuidSchema,
});
export type PrepSplitsParams = z.infer<typeof PrepSplitsParamsSchema>;

export const PrepSplitItemSchema = z.object({
  split_id:              uuidSchema,
  split_sequence:        z.number(),
  container_identifier:  z.string(),
  volume_ml:             z.string(),
  status:                z.string(),
  label_expiry_at:       z.string().nullable(),
  is_expired:            z.boolean(),
  expiry_notes:          z.string().nullable(),
  is_restored:           z.boolean(),
  restored_from_split_id: uuidSchema.nullable(),
  delivery_id:           uuidSchema.nullable(),
  sec:                   FeedPrepSplitSecSchema.optional(),
});
export type PrepSplitItem = z.infer<typeof PrepSplitItemSchema>;

export const PrepSplitsResponseSchema = z.object({
  prep_id:              uuidSchema,
  splits:               z.array(PrepSplitItemSchema),
});
export type PrepSplitsResponse = z.infer<typeof PrepSplitsResponseSchema>;

// ── GET /feedPrep/patient-feeds/:patient_id — All pending feeds for a patient ─

export const PatientFeedsParamsSchema = z.object({
  patient_id: uuidSchema,
});
export type PatientFeedsParams = z.infer<typeof PatientFeedsParamsSchema>;

export const PatientFeedsQuerySchema = z.object({});
export type PatientFeedsQuery = z.infer<typeof PatientFeedsQuerySchema>;

export const PatientFeedItemSchema = z.object({
  schedule_id:        uuidSchema,
  order_id:           uuidSchema,
  order_identifier:   z.string().nullable(),
  start_time:         z.string(),
  order_base:         z.string().nullable(),
  volume_ml_per_feed: z.string(),
});
export type PatientFeedItem = z.infer<typeof PatientFeedItemSchema>;

export const PatientFeedsResponseSchema = z.object({
  items: z.array(PatientFeedItemSchema),
  total: z.number(),
});
export type PatientFeedsResponse = z.infer<typeof PatientFeedsResponseSchema>;

// ── GET /feedPrep/patient-history/:patient_id — Feed history for a patient ────

export const PatientHistoryParamsSchema = z.object({
  patient_id: uuidSchema,
});
export type PatientHistoryParams = z.infer<typeof PatientHistoryParamsSchema>;

export const PatientHistoryQuerySchema = z.object({
  status: z.enum(['PENDING', 'DUE', 'COMPLETED', 'CANCELLED']).optional(),
  days: z.coerce.number().int().positive().optional(),
});
export type PatientHistoryQuery = z.infer<typeof PatientHistoryQuerySchema>;

export const PatientHistoryBottleSchema = z.object({
  container_identifier:   z.string().nullable(),
  syringe_label_barcode:  z.string().nullable(),
  volume_ml:              z.string().nullable(),
  route:                  z.string().nullable(),
  label_expiry_at:        z.string().nullable(),
});
export type PatientHistoryBottle = z.infer<typeof PatientHistoryBottleSchema>;

export const PatientHistoryAdministrationSchema = z.object({
  delivery_id:           uuidSchema,
  delivered_at:          z.string().nullable(),
  delivered_by:          uuidSchema.nullable(),
  delivery_method:       z.string().nullable(),
  actual_volume_ml:      z.string().nullable(),
  feeding_started_at:    z.string().nullable(),
  feeding_completed_at:  z.string().nullable(),
  verification_result:   z.string().nullable(),
  bottle:                PatientHistoryBottleSchema.nullable(),
});
export type PatientHistoryAdministration = z.infer<typeof PatientHistoryAdministrationSchema>;

export const PatientHistoryItemSchema = z.object({
  type:               z.enum(['SCHEDULED', 'QUICK']),
  schedule_id:        uuidSchema.nullable(),
  prep_id:            uuidSchema.nullable(),
  order_id:           uuidSchema.nullable(),
  order_identifier:   z.string().nullable(),
  start_time:         z.string(),
  end_time:           z.string().nullable(),
  order_base:         z.string().nullable(),
  volume_ml_per_feed: z.string(),
  status:             z.enum(['PENDING', 'DUE', 'COMPLETED', 'CANCELLED']),
  administration:     PatientHistoryAdministrationSchema.nullable(),
});
export type PatientHistoryItem = z.infer<typeof PatientHistoryItemSchema>;

export const PatientHistoryResponseSchema = z.object({
  items: z.array(PatientHistoryItemSchema),
  total: z.number(),
});
export type PatientHistoryResponse = z.infer<typeof PatientHistoryResponseSchema>;

// ── GET /feedPrep/:prep_id — Lookup preparation by prep_id ────────────────

export const PrepByIdParamsSchema = z.object({
  prep_id: uuidSchema,
});
export type PrepByIdParams = z.infer<typeof PrepByIdParamsSchema>;

export const PrepByIdSplitSchema = z.object({
  split_id:              uuidSchema,
  split_sequence:        z.number(),
  volume_ml:             z.string(),
  status:                z.string(),
  container_identifier:  z.string().nullable(),
  label_expiry_at:       z.string().nullable(),
  is_expired:            z.boolean(),
  expiry_notes:          z.string().nullable(),
  is_restored:           z.boolean(),
  restored_from_split_id: uuidSchema.nullable(),
  delivery_id:           uuidSchema.nullable(),
  sec:                   FeedPrepSplitSecSchema.optional(),
});

export const PrepByIdResponseSchema = z.object({
  prep_id:              uuidSchema,
  container_identifier: z.string().nullable(),
  prep_type:            z.string(),
  status:               z.string(),
  patient_id:           uuidSchema.nullable(),
  patient_name:         z.string().nullable(),
  mrn:                  z.string().nullable(),
  hospital_id:          uuidSchema,
  prepared_by:          uuidSchema,
  actual_volume_ml:     z.string().nullable(),
  route:                z.string().nullable(),
  order_id:             uuidSchema.nullable(),
  order_type:           z.string().nullable(),
  allergy_check_passed: z.boolean().nullable(),
  expiry_check_passed:  z.boolean().nullable(),
  recall_check_passed:  z.boolean().nullable(),
  started_at:           z.string().nullable(),
  completed_at:         z.string().nullable(),
  created_at:           z.string(),
  splits:               z.array(PrepByIdSplitSchema),
  items:                z.array(ContainerInfoItemSchema),
  mother_id:            uuidSchema.nullable(),
  mother_name:          z.string().nullable(),
  mother_mrn:           z.string().nullable(),
  date_of_birth:        z.string().nullable(),
  unit:                 z.string().nullable(),
  bed:                  z.string().nullable(),
});
export type PrepByIdResponse = z.infer<typeof PrepByIdResponseSchema>;
